import React, { useEffect, useState } from 'react';
import { Link, useLocation, NavLink } from 'react-router-dom';
import { Menu, X, MapPin, Phone, Mail, Instagram } from 'lucide-react';
export function Layout({
  children
}: {
  children: React.ReactNode;
}) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 40);
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    setIsMobileMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);
  // Header styling logic
  // - On home: transparent over hero, solid black after scroll
  // - On other pages: always solid black
  const useTransparentHeader = isHome && !isScrolled;
  const headerBg = useTransparentHeader ? 'bg-transparent' : 'bg-reach-black';
  const navItems = [{
    to: '/',
    label: 'Home'
  }, {
    to: '/programs',
    label: 'Programs'
  }, {
    to: '/fall-2026',
    label: 'Fall 2026'
  }, {
    to: '/about',
    label: 'About'
  }, {
    to: '/pricing',
    label: 'Pricing'
  }, {
    to: '/contact',
    label: 'Contact'
  }];
  return <div className="min-h-screen flex flex-col bg-reach-white" data-id="element-19" data-mp="zumb8lt8uyj1">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${headerBg} ${isScrolled ? 'py-2 border-b border-white/10' : 'py-2 md:py-3'}`} data-id="element-20" data-mp="13u4m0orcpt85">
        <div className="max-w-container mx-auto px-4 md:px-8 flex items-center justify-between" data-id="element-21" data-mp="1dtlnetzklkyb">
          {/* Logo — white horizontal banner on dark header */}
          <Link to="/" className="flex items-center z-50 shrink-0" aria-label="Reach Performance Home" data-id="element-22" data-mp="2kb8tn72vewqt">
            <img src="https://cdn.magicpatterns.com/uploads/esRWbJLRpNThsrPH4a2raJ/Reach-Banner-Wht.png" alt="Reach Performance" className="h-24 md:h-32 w-auto object-contain" data-id="element-23" data-mp="1zjakg3h1itvi" />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-7" data-id="element-24" data-mp="24jg7f6cd7s79">
            {navItems.map(item => <NavLink key={item.to} to={item.to} end={item.to === '/'} className={({
            isActive
          }) => `font-inter text-sm font-medium uppercase tracking-wider transition-colors text-white hover:text-reach-red ${isActive ? 'text-reach-red' : ''}`} data-id="element-25" data-mp="1gt2ms0w40vmp">
                {item.label}
              </NavLink>)}
          </nav>

          {/* Right side actions */}
          <div className="hidden lg:flex items-center gap-5" data-id="element-26" data-mp="t41a21g8uybm">
            <Link to="/book" className="font-inter text-sm font-medium uppercase tracking-wider text-white hover:text-reach-red transition-colors" data-id="element-27" data-mp="t3i0b80qzg8o">
              Book Consultation
            </Link>
            <Link to="/pricing" className="btn-primary !min-h-[40px] !px-5 !text-xs" data-id="element-28" data-mp="2ut77kym58cf1">
              Enroll Now
            </Link>
          </div>

          {/* Mobile Hamburger */}
          <button className="lg:hidden z-50 p-2 text-white" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'} aria-expanded={isMobileMenuOpen} data-id="element-29" data-mp="3nhl419xl30oh">
            {isMobileMenuOpen ? <X size={28} data-id="element-30" data-mp="3iii0xv15z4j5" /> : <Menu size={28} data-id="element-31" data-mp="3ii04axalaoap" />}
          </button>
        </div>

        {/* Mobile Nav Overlay */}
        <div className={`fixed inset-0 bg-reach-black z-40 flex flex-col pt-28 px-6 transition-transform duration-300 ease-in-out lg:hidden ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`} data-id="element-32" data-mp="231rimxx46dln">
          <nav className="flex flex-col gap-1" data-id="element-33" data-mp="2nbiwyf3p7yzz">
            {navItems.map(item => <NavLink key={item.to} to={item.to} end={item.to === '/'} className={({
            isActive
          }) => `font-bebas text-white text-4xl tracking-wide border-b border-white/10 py-4 ${isActive ? 'text-reach-red' : ''}`} data-id="element-34" data-mp="2iizjrj8xb5vp">
                {item.label}
              </NavLink>)}
          </nav>
          <div className="mt-auto pb-12 flex flex-col gap-3" data-id="element-35" data-mp="31b1vqhamdxzm">
            <Link to="/pricing" className="btn-primary w-full" data-id="element-36" data-mp="2waw8refaoznv">
              Enroll Now
            </Link>
            <Link to="/book" className="btn-secondary-dark w-full" data-id="element-37" data-mp="2salrv6pysou8">
              Book Free Consultation
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content — home is full-bleed (hero handles top space); other pages get top padding */}
      <main className={`flex-grow ${isHome ? '' : 'pt-32 md:pt-40'}`} data-id="element-38" data-mp="3si0r214dt2pz">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-reach-black text-white pt-16 pb-8" data-id="element-39" data-mp="dgcdnw1lsob">
        <div className="max-w-container mx-auto px-4 md:px-8" data-id="element-40" data-mp="bvf3qicfvmr">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12" data-id="element-41" data-mp="5brmlzq92572">
            {/* Column 1: Brand wordmark + tagline */}
            <div data-id="element-42" data-mp="f34dop484rc1">
              <img src="https://cdn.magicpatterns.com/uploads/esRWbJLRpNThsrPH4a2raJ/Reach-Banner-Wht.png" alt="Reach Performance" className="w-[200px] h-auto object-contain mb-5" data-id="element-43" data-mp="f3nlve42o1cv" />
              <p className="font-bebas text-reach-red text-sm tracking-[0.2em] mb-5" data-id="element-44" data-mp="o4lq9xifaavz">
                STABILITY · STRENGTH · POWER
              </p>
              <p className="font-inter text-sm text-gray-400 leading-relaxed" data-id="element-45" data-mp="2pvp22q41tvi4">
                Athletic performance training for athletes ages 8 to 18 in North
                Texas.
              </p>
            </div>

            {/* Column 2: Programs */}
            <div data-id="element-46" data-mp="21w2y7o1pvf4">
              <h3 className="font-bebas text-xl tracking-widest text-reach-red mb-5" data-id="element-47" data-mp="3t62wlbvy7f1f">
                PROGRAMS
              </h3>
              <ul className="space-y-3 font-inter text-sm text-gray-300" data-id="element-48" data-mp="b0q7ck7mbq38">
                <li data-id="element-49" data-mp="61dd3eef3gqm">
                  <Link to="/programs" className="hover:text-white transition-colors" data-id="element-50" data-mp="613uibvpcbuy">
                    Advanced Performance
                  </Link>
                </li>
                <li data-id="element-51" data-mp="fz1gesq39wke">
                  <a href="/#pro-athletes" className="hover:text-white transition-colors" data-id="element-52" data-mp="fxgmn5blmhnl">
                    Pro Athletes
                  </a>
                </li>
                <li data-id="element-53" data-mp="13ovt9aaerl4n">
                  <Link to="/programs" className="hover:text-white transition-colors" data-id="element-54" data-mp="13occvy0ep4sq">
                    Performance Prep
                  </Link>
                </li>
                <li data-id="element-55" data-mp="1icdo7gy651dv">
                  <Link to="/fall-2026" className="hover:text-white transition-colors" data-id="element-56" data-mp="1i9rlpvq4xc2i">
                    Homeschool Multisport
                  </Link>
                </li>
                <li data-id="element-57" data-mp="1dc84cmy54j6c">
                  <Link to="/book" className="hover:text-white transition-colors" data-id="element-58" data-mp="1dbq2nasplcr1">
                    Free Consultation
                  </Link>
                </li>
              </ul>
            </div>

            {/* Column 3: Resources */}
            <div data-id="element-59" data-mp="po86z46bpuis">
              <h3 className="font-bebas text-xl tracking-widest text-reach-red mb-5" data-id="element-60" data-mp="poq5y8kdemue">
                RESOURCES
              </h3>
              <ul className="space-y-3 font-inter text-sm text-gray-300" data-id="element-61" data-mp="gmgzbts83dma">
                <li data-id="element-62" data-mp="gna7mz6tyb6j">
                  <Link to="/about" className="hover:text-white transition-colors" data-id="element-63" data-mp="gouv64uq8tuw">
                    About
                  </Link>
                </li>
                <li data-id="element-64" data-mp="w4rs4p96icdb">
                  <Link to="/fall-2026" className="hover:text-white transition-colors" data-id="element-65" data-mp="w48kc1d8t4d0">
                    Fall 2026
                  </Link>
                </li>
                <li data-id="element-66" data-mp="m5srntykhn1e">
                  <Link to="/pricing" className="hover:text-white transition-colors" data-id="element-67" data-mp="m8ezlupde4k5">
                    Pricing
                  </Link>
                </li>
                <li data-id="element-68" data-mp="8exfo8sruptl">
                  <Link to="/contact" className="hover:text-white transition-colors" data-id="element-69" data-mp="8ee9fpchhpve">
                    Contact
                  </Link>
                </li>
                <li data-id="element-70" data-mp="3ul2r0ub2oe5h">
                  <Link to="/book" className="hover:text-white transition-colors" data-id="element-71" data-mp="3ulmaifgdfalm">
                    Book Consultation
                  </Link>
                </li>
              </ul>
            </div>

            {/* Column 4: Get in Touch */}
            <div data-id="element-72" data-mp="2a2a2nfkxszjq">
              <h3 className="font-bebas text-xl tracking-widest text-reach-red mb-5" data-id="element-73" data-mp="2a5xw6eqdtlyg">
                GET IN TOUCH
              </h3>
              <ul className="space-y-4 font-inter text-sm text-gray-300" data-id="element-74" data-mp="2k2st4t70beam">
                <li className="flex items-start gap-3" data-id="element-75" data-mp="1meirifb4usk1">
                  <MapPin size={18} className="shrink-0 mt-0.5 text-reach-red" data-id="element-76" data-mp="1mg3lo3tjc9hd" />
                  <a href="https://maps.google.com/?q=6051+Alma+Rd,+McKinney,+TX+75070" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors leading-relaxed" data-id="element-77" data-mp="1wcorgrddpom9">
                    INPOWERiQ Performance Center
                    <br data-id="element-78" data-mp="18m2jt17ae6cs" />
                    6051 Alma Rd
                    <br data-id="element-79" data-mp="itnmnb4w93ob" />
                    McKinney, TX 75070
                  </a>
                </li>
                <li className="flex items-center gap-3" data-id="element-80" data-mp="nsgpm9mk1ca8">
                  <Phone size={18} className="shrink-0 text-reach-red" data-id="element-81" data-mp="nqvvum82dxdf" />
                  <a href="tel:7089804666" className="hover:text-white transition-colors" data-id="element-82" data-mp="ssv2c4z3pael">
                    (708) 980-4666
                  </a>
                </li>
                <li className="flex items-center gap-3" data-id="element-83" data-mp="4z6t3l9poje">
                  <Mail size={18} className="shrink-0 text-reach-red" data-id="element-84" data-mp="51fujwok20vy" />
                  <a href="mailto:Fernando@reachperformancehq.com" className="hover:text-white transition-colors break-all" data-id="element-85" data-mp="a1c0i1gtc2e5">
                    Fernando@reachperformancehq.com
                  </a>
                </li>
                <li className="flex items-center gap-3" data-id="element-86" data-mp="3dfy5q79slwjs">
                  <Instagram size={18} className="shrink-0 text-reach-red" data-id="element-87" data-mp="3ig3rj6tdsoty" />
                  <a href="https://instagram.com/reachperformancehq" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors" data-id="element-88" data-mp="1zmj1s0tn2qjo">
                    @reachperformancehq
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="border-t border-white/10 pt-6 flex flex-col md:flex-row justify-between items-center gap-3" data-id="element-89" data-mp="1go6r2an2x8lz">
            <p className="font-inter text-xs text-gray-500" data-id="element-90" data-mp="1buvj266j6lsh">
              © 2026 Reach Performance LLC. All rights reserved.
            </p>
            <p className="font-bebas text-xs tracking-[0.25em] text-gray-500" data-id="element-91" data-mp="12vqmh5ej52iy">
              STABILITY · STRENGTH · POWER
            </p>
          </div>
        </div>
      </footer>
    </div>;
}